import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

export interface AreaProgressRow {
  area: string;
  /** 0–100, ya redondeado. */
  pct: number;
  done: number;
  total: number;
}

interface Props<T extends AreaProgressRow> {
  /** Ya viene ordenado (de mayor a menor %). */
  rows: T[];
  /** Texto del tooltip de cada fila: [valor, nombre]. */
  tooltip: (row: T) => [string, string];
}

/** Verde al llegar, teal en camino, gris si todavía nadie cerró nada. */
function barColor(pct: number): string {
  if (pct >= 100) return '#10B981';
  if (pct === 0) return '#D1D5DB';
  return '#0D9488';
}

/**
 * % completado por área, barras horizontales con "82% · 9/11" a la derecha
 * (pedido Sol 14/09 "para mandar por el canal": se lee de un vistazo y se saca
 * screenshot). Lo usan el Mapa de Talento y D-Talk (feedback 30/09: "que se vea
 * el % completado en cada área, como el del Mapa").
 *
 * La etiqueta va en un segundo eje Y a la derecha y no en un LabelList: así
 * sale también en las áreas con 0% (una barra de ancho 0 no dibuja su label)
 * y queda en una columna que no se corta en dos renglones. Por lo mismo, el
 * fondo gris es una barra-riel al 100% y no el `background` de la barra.
 */
export function AreaProgressChart<T extends AreaProgressRow>({ rows, tooltip }: Props<T>) {
  const data = rows.map(r => ({ ...r, tag: `${r.pct}% · ${r.done}/${r.total}`, track: 100 }));
  // ~34px por fila para que las etiquetas no se pisen, con un mínimo cómodo
  const height = Math.max(160, data.length * 34 + 16);
  const tick = { fontSize: 12, fill: 'currentColor' };

  return (
    <ResponsiveContainer width="100%" height={height}>
      {/* barGap = -barSize: el riel (100%) y la barra se superponen en la misma fila */}
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 8, bottom: 4, left: 8 }} barSize={20} barGap={-20}>
        <XAxis type="number" domain={[0, 100]} hide />
        <YAxis yAxisId="area" type="category" dataKey="area" width={150} tick={tick} tickLine={false} axisLine={false} />
        <YAxis yAxisId="tag" orientation="right" type="category" dataKey="tag" width={112} tick={tick} tickLine={false} axisLine={false} />
        <Tooltip
          cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
          formatter={(_v: unknown, _n: unknown, item: { payload?: T }) => (item.payload ? tooltip(item.payload) : ['', ''])}
          // con dos ejes Y el título por defecto es el índice de la fila: se pone el área
          labelFormatter={(_l: unknown, payload: ReadonlyArray<{ payload?: T }>) => payload?.[0]?.payload?.area ?? ''}
          contentStyle={{ fontSize: 12 }}
        />
        <Bar yAxisId="area" dataKey="track" fill="hsl(var(--muted))" radius={4} isAnimationActive={false} tooltipType="none" />
        <Bar yAxisId="area" dataKey="pct" radius={[0, 4, 4, 0]}>
          {data.map(r => <Cell key={r.area} fill={barColor(r.pct)} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
